import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CheckCircle, XCircle, HourglassSimple, Trash, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { AdminPunchButton } from '../components/time-entries/AdminPunchModal';
import { WorkedDaysSummary } from '../components/time-entries/WorkedDaysSummary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyHoursPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);

  const fetchEntries = useCallback(async () => {
    try {
      const calls = [axios.get(`${API_URL}/api/time-entries`, { withCredentials: true })];
      if (isAdmin) {
        calls.push(axios.get(`${API_URL}/api/users/employees`, { withCredentials: true }).catch(() => ({ data: [] })));
        calls.push(axios.get(`${API_URL}/api/projects`, { withCredentials: true }).catch(() => ({ data: [] })));
      }
      const results = await Promise.all(calls);
      setEntries(results[0].data);
      if (isAdmin) {
        setEmployees(results[1]?.data || []);
        setProjects(results[2]?.data || []);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des heures');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleDelete = async (entryId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée ?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/time-entries/${entryId}`, { withCredentials: true });
      toast.success('Entrée supprimée');
      fetchEntries();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved"><CheckCircle size={14} /> Validé</span>;
      case 'rejected':
        return <span className="status-badge rejected"><XCircle size={14} /> Rejeté</span>;
      default:
        return <span className="status-badge pending"><HourglassSimple size={14} /> En attente</span>;
    }
  };

  const filteredEntries = entries.filter(entry => {
    if (filter === 'all') return true;
    return entry.status === filter;
  });

  const totalHours = filteredEntries.reduce((sum, e) => sum + e.total_hours, 0);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="my-hours-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Mes heures</h1>
          <p className="page-subtitle">Historique de vos saisies</p>
        </div>
        {isAdmin && (
          <AdminPunchButton employees={employees} projects={projects} onCreated={fetchEntries} />
        )}
      </div>

      {!isAdmin && (
        <div style={{ marginBottom: 20 }}>
          <WorkedDaysSummary />
        </div>
      )}

      <div className="tabs">
        <button 
          className={`tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
          data-testid="filter-all"
        >
          Toutes ({entries.length})
        </button>
        <button 
          className={`tab ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
          data-testid="filter-pending"
        >
          En attente ({entries.filter(e => e.status === 'pending').length})
        </button>
        <button 
          className={`tab ${filter === 'approved' ? 'active' : ''}`}
          onClick={() => setFilter('approved')}
          data-testid="filter-approved"
        >
          Validées ({entries.filter(e => e.status === 'approved').length})
        </button>
        <button 
          className={`tab ${filter === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilter('rejected')}
          data-testid="filter-rejected"
        >
          Rejetées ({entries.filter(e => e.status === 'rejected').length})
        </button>
      </div>

      <div style={{ 
        backgroundColor: '#F4F5F7', 
        padding: '16px 24px', 
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '1px solid #E2E2E6'
      }}>
        <span className="label-text">Total heures affichées</span>
        <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#002FA7' }}>
          {Math.round(totalHours * 100) / 100}h
        </span>
      </div>

      {filteredEntries.length > 0 ? (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Début</th>
                <th>Fin</th>
                <th>Pause</th>
                <th>Total</th>
                <th>Statut</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <tr key={entry.id} data-testid={`entry-row-${entry.id}`}>
                  <td>{format(parseISO(entry.date), 'EEEE dd/MM', { locale: fr })}</td>
                  <td>{entry.start_time}</td>
                  <td>{entry.end_time}</td>
                  <td>{entry.break_duration} min</td>
                  <td style={{ fontWeight: '600' }}>{Math.round(entry.total_hours * 100) / 100}h</td>
                  <td>{getStatusBadge(entry.status)}</td>
                  <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.notes || '-'}
                  </td>
                  <td>
                    {entry.status === 'pending' && (
                      <button
                        data-testid={`delete-entry-${entry.id}`}
                        className="btn btn-icon btn-danger btn-sm"
                        onClick={() => handleDelete(entry.id)}
                        title="Supprimer"
                      >
                        <Trash size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Clock size={48} className="empty-state-icon" />
          <p className="empty-state-text">Aucune entrée trouvée</p>
        </div>
      )}
    </div>
  );
};

export default MyHoursPage;
